import { createContext, useCallback, useMemo, useReducer } from 'react';

import { ROLE_BUILDER_STEP, canAdvance, findRoleByName, grantsForStudios, isRoleNameTaken } from '../utils/roleBuilderRules';
import { createRoleBuilderState, roleBuilderReducer } from './roleBuilderReducer';

export const RoleBuilderContext = createContext(null);

const STEP_ORDER = Object.values(ROLE_BUILDER_STEP);

/**
 * State of the New role wizard (name, description, Start from, studios, permission draft,
 * members, current step) plus what is derived from it. Read it with `useRoleBuilder`.
 *
 * @param {object} props
 * @param {object[]} props.studios Catalog studios with their `tree`.
 * @param {object[]} props.roles Existing roles (Start from chips, duplicate-name check).
 */
const RoleBuilderProvider = ({ studios, roles, children }) => {
  const nodes = useMemo(() => studios.flatMap((studio) => studio.tree), [studios]);
  const [state, dispatch] = useReducer(roleBuilderReducer, nodes, createRoleBuilderState);

  const setName = useCallback((name) => dispatch({ type: 'setName', name }), []);
  const setDescription = useCallback((description) => dispatch({ type: 'setDescription', description }), []);
  const chooseStartFrom = useCallback(
    (roleId) => dispatch({ type: 'startFrom', nodes, role: roles.find((role) => role.id === roleId) ?? null }),
    [nodes, roles],
  );
  const toggleStudio = useCallback((studioId) => dispatch({ type: 'toggleStudio', studioId }), []);
  const togglePermission = useCallback((id, on) => dispatch({ type: 'togglePermission', nodes, id, on }), [nodes]);
  const setPermissionLevel = useCallback((id, level) => dispatch({ type: 'setPermissionLevel', nodes, id, level }), [nodes]);
  const toggleMember = useCallback((memberId) => dispatch({ type: 'toggleMember', memberId }), []);
  const goTo = useCallback((step) => dispatch({ type: 'goTo', step }), []);
  const reportNameConflict = useCallback((name) => dispatch({ type: 'nameConflict', name }), []);

  const grants = useMemo(
    () => grantsForStudios(state.permissionDraft, studios, state.studioIds),
    [state.permissionDraft, studios, state.studioIds],
  );
  const nameTaken = useMemo(
    () =>
      isRoleNameTaken(state.name, roles) ||
      (state.conflictName !== null && state.conflictName.trim().toLowerCase() === state.name.trim().toLowerCase()),
    [state.name, state.conflictName, roles],
  );
  // The name to show in "already exists": the existing role's own spelling, else what was typed.
  const takenName = useMemo(() => findRoleByName(state.name, roles)?.name ?? state.name.trim(), [state.name, roles]);
  const stepIndex = STEP_ORDER.indexOf(state.currentStep);
  const canGoNext = canAdvance(state.currentStep, { ...state, grantedCount: grants.length }, roles);

  const goNext = useCallback(() => goTo(STEP_ORDER[Math.min(stepIndex + 1, STEP_ORDER.length - 1)]), [goTo, stepIndex]);
  const goBack = useCallback(() => goTo(STEP_ORDER[Math.max(stepIndex - 1, 0)]), [goTo, stepIndex]);

  const value = useMemo(
    () => ({
      ...state,
      studios,
      roles,
      grants,
      grantedCount: grants.length,
      nameTaken,
      takenName,
      stepIndex,
      stepIds: STEP_ORDER,
      isFirstStep: stepIndex === 0,
      isLastStep: stepIndex === STEP_ORDER.length - 1,
      canGoNext,
      setName,
      setDescription,
      chooseStartFrom,
      toggleStudio,
      togglePermission,
      setPermissionLevel,
      toggleMember,
      goTo,
      goNext,
      goBack,
      reportNameConflict,
    }),
    [
      state,
      studios,
      roles,
      grants,
      nameTaken,
      takenName,
      stepIndex,
      canGoNext,
      setName,
      setDescription,
      chooseStartFrom,
      toggleStudio,
      togglePermission,
      setPermissionLevel,
      toggleMember,
      goTo,
      goNext,
      goBack,
      reportNameConflict,
    ],
  );

  return <RoleBuilderContext.Provider value={value}>{children}</RoleBuilderContext.Provider>;
};

export default RoleBuilderProvider;
